import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { parseDoubleEscapedJson } from '../utils/fixDoubleEscapedJSON';
import { useLocation, useNavigate } from 'react-router-dom';


// rewrite glossary links to function correctly
function rewriteGlossaryLinks(htmlString) {
  if (!htmlString) return htmlString;

  const parser = new DOMParser();
  const doc = parser.parseFromString(htmlString, 'text/html');

  const anchors = doc.querySelectorAll('a[data-ids]');
  anchors.forEach(a => {
    const id = a.getAttribute('data-ids')?.replace(/"/g, '');
    const baseWithHash = window.location.href.split('#')[0] + '#';
    if (id) {
      a.setAttribute(
        'href',
        `${baseWithHash}/search?id=${id}`
      );
    }
  });

  return doc.body.innerHTML;
}

function HtmlContent({ htmlString }) {
  return <div dangerouslySetInnerHTML={{ __html: htmlString }} />;
}

function containsHTML(str) {
  const htmlRegex = /<\/?[a-z][\s\S]*>/i;
  return htmlRegex.test(str);
}


const GlossaryCard = ({ term, longidObj }) => {

  const [longid, setLongid] = useState('');
  const [showAll, setShowAll] = useState(false);
  const [extraData, setExtraData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [glossaryData, setGlossaryData] = useState(null);


  useEffect(() => {
        const observer = new MutationObserver(() => {
          const el =
            document.querySelector('[data-longid]') ||
            document.getElementById('app');

          const foundLongId = el?.dataset?.longid;

          if (foundLongId) {
            setLongid(foundLongId);
            observer.disconnect();
          }
        });

        observer.observe(document.body, {
          childList: true,
          subtree: true,
        });

        return () => observer.disconnect();
      }, []);

  const baseFields = [
    { label: 'Data Steward', value: term.stewards?.[0]?.name },
    { label: 'Data Community', value: term.community?.name },
    { label: 'Last Updated', value: term.lastUpdated },
    { label: 'Status', value: term.status },
  ];

const allowedValues = extraData?.allowedValues;


  const enumField =
  allowedValues?.length > 9
    ? (
        <HtmlContent
          htmlString={`<a href="/reference-data-terms#/reference-data?tid=${term.id}">See reference data description</a>`}
        />
      )
    : allowedValues?.map(s => s.value).join(', ') || '';

  const extraFields = [
    { label: 'System of Record', value: extraData?.systemOfRecord?.value },
    { label: 'Example/usage', value: <HtmlContent htmlString={extraData?.description} /> },
    { label: 'UDM entity/attribute', value: extraData?.udmEntity },
    { label: 'Rule/calculation', value: <HtmlContent htmlString={extraData?.rules} /> },
    { label: 'Reference data/enumeration', value: enumField },
    { label: 'Acronym', value: extraData?.acronyms?.map(s => s.value).join(', ') || '' },
    { label: 'Synonym', value: extraData?.synonyms?.map(s => s.value).join(', ') || '' },
    { label: 'Data Standard', value: <HtmlContent htmlString={term.standardURL} /> },
    { label: 'University Policy', value: <HtmlContent htmlString={term.policyURL} /> },
  ];

  const visibleFields = showAll ? [...baseFields, ...extraFields] : baseFields;

  const handleToggle = async () => {
    if (!showAll && !extraData) {
      setLoading(true);
      setError(null);
      const url = longid ? `/edgconnect/term/id/${term.id}?longid=${longid}` : `/edgconnect/term/id/${term.id}?longid=90fa602c`
      try {
        const response = await axios.get(`${url}`);
        if (typeof response.data === 'string') {
          try {
            const fixedData = JSON.parse(response.data);
            setExtraData(fixedData.pageItems[0]);
          } catch (e) {
            console.error('Failed to parse JSON:', e);
          }
        } else {
          setExtraData(response.data);
        }
      } catch (err) {
        console.error('Failed to load extra term data:', err);
        setError('Failed to load additional details');
      } finally {
        setLoading(false);
      }
    }
    setShowAll(prev => !prev);
  };

  const toSlug = str => str?.replace(/\s+/g, '')
  const riskLevel = term.sensitivity?.[0]?.toLowerCase() || 'unknown';
  const location = useLocation();
  const currentPage = location.pathname;
  const baseWithHash = window.location.href.split('#')[0] + '#';
  const url = `${baseWithHash}${currentPage === "/a-z" ? "/search" : currentPage}?glossary=${encodeURIComponent(toSlug(term.glossary?.name))}`;

  return (
    <div
      className="p-4 mb-4 bg-white rounded-lg shadow relative before--absolute before--content-[''] before--block before--w-[.5rem] before--h-[4rem] before--rounded-tl-lg before--top-0 before--left-0 search-result"
      data-risk={riskLevel.toLowerCase().replace(/\s+/g, '-')}
    >
      <h2 className="mb-3 text-xl font-semibold text-medium-blue">{term.name}</h2>

      <p className="mb-2 text-sm text-grey-500">
        {term.community?.name} &gt; {glossaryData?.link ?? <a href={url} >{term.glossary?.name}</a>}
      </p>

      <p className="font-semibold text-dark-blue">
        Security Classification: {term.sensitivity?.[0]}
      </p>

      {Array.isArray(term.definition) ? term.definition.map((def, idx) => (
        <div key={idx} className="mb-4 text-dark-blue">
          {containsHTML(def) ? (
            <HtmlContent htmlString={rewriteGlossaryLinks(def)} />
          ) : (
            <p>{def}</p>
          )}
        </div>
      )) : (
        term.definition ? (
          containsHTML(term.definition) ? (
            <HtmlContent htmlString={rewriteGlossaryLinks(term.definition)} />
          ) : (
            <p className="mb-4 text-dark-blue">{term.definition}</p>
          )
        ) : null
      )}

      <dl className="">
        {visibleFields.map((field, index) => (
          <div className="flex flex-wrap w-full text-sm" key={index}>
            <dt className="m-0 p-2 font-semibold border-b border-[rgba(44,62,80,.5)] w-one-third">{field.label}:</dt>
            <dd className="m-0 p-2 border-b border-[rgba(44,62,80,.5)] w-two-thirds">{field.value || ''}</dd>
          </div>
        ))}
      </dl>

      {error && <p className="mt-2 text-sm text-high-risk">{error}</p>}
      {loading && <p className="mt-2 text-sm text-muted">Loading...</p>}

      <button
        onClick={handleToggle}
        disabled={loading}
        className="p-2 mt-4 text-xs bg-grey-200 disabled--opacity-50"
      >
        {showAll ? 'Show Less' : 'Show More'}
      </button>
    </div>
  );
};

export default GlossaryCard;
